import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Clock, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = {
  pending_review: { label: 'Pendiente', color: 'bg-yellow-500', text: 'text-yellow-400' },
  needs_info: { label: 'Info Requerida', color: 'bg-orange-500', text: 'text-orange-400' },
  accepted: { label: 'Aceptada', color: 'bg-blue-500', text: 'text-blue-400' },
  in_progress: { label: 'En Progreso', color: 'bg-purple-500', text: 'text-purple-400' },
  ready_to_pay: { label: 'Listo Pagar', color: 'bg-cyan-500', text: 'text-cyan-400' },
  paid: { label: 'Pagada', color: 'bg-green-500', text: 'text-green-400' },
  delivered: { label: 'Entregada', color: 'bg-green-600', text: 'text-green-400' },
};

const MonitorPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/monitor/${token}`);
      setData(res.data);
      setLastUpdate(new Date());
      setError(false);
    } catch {
      setError(true);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    const interval = setInterval(fetchData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <Loader2 className="w-10 h-10 animate-spin text-red-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-white text-lg">Link no válido</p>
      </div>
    </div>
  );

  const tickets = data?.tickets || [];
  const counts = data?.status_counts || {};

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="monitor-page">
      {/* Header */}
      <div className="bg-[#111] border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-ar.png" alt="" className="w-8 h-8" />
            <div>
              <h1 className="font-bold text-lg tracking-tight">AR PERFORMANCE — MONITOR</h1>
              <p className="text-zinc-500 text-xs">Tickets en cola en tiempo real</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 text-green-400 text-xs">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              EN VIVO
            </div>
            {lastUpdate && <span className="text-zinc-600 text-xs">{lastUpdate.toLocaleTimeString('es-ES')}</span>}
          </div>
        </div>
      </div>

      {/* Status summary */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="bg-[#111] border border-white/10 rounded px-4 py-2 text-center">
            <p className="text-2xl font-bold text-red-500 font-mono">{data?.total || 0}</p>
            <p className="text-zinc-500 text-[10px] uppercase">Total Cola</p>
          </div>
          {Object.entries(counts).map(([status, count]) => {
            const s = STATES[status] || { label: status, text: 'text-zinc-400' };
            return (
              <div key={status} className="bg-[#111] border border-white/10 rounded px-4 py-2 text-center">
                <p className={`text-2xl font-bold font-mono ${s.text}`}>{count}</p>
                <p className="text-zinc-500 text-[10px] uppercase">{s.label}</p>
              </div>
            );
          })}
        </div>

        {/* Ticket list */}
        {tickets.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500 text-lg">Sin tickets en cola</p>
          </div>
        ) : (
          <div className="bg-[#111] border border-white/10 rounded overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-[10px] text-zinc-500 uppercase tracking-wider">
                  <th className="text-left px-4 py-3">#</th>
                  <th className="text-left px-3 py-3">ESTADO</th>
                  <th className="text-left px-3 py-3">CLIENTE</th>
                  <th className="text-left px-3 py-3">VEHICULO</th>
                  <th className="text-left px-3 py-3">MATRICULA</th>
                  <th className="text-left px-3 py-3">SERVICIO</th>
                  <th className="text-left px-3 py-3">ECU</th>
                  <th className="text-left px-3 py-3">TIEMPO</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t, i) => {
                  const s = STATES[t.status] || { label: t.status, color: 'bg-zinc-500', text: 'text-zinc-400' };
                  const created = new Date(t.created_at);
                  const ago = Math.floor((Date.now() - created.getTime()) / 60000);
                  const timeStr = ago < 60 ? `${ago}m` : ago < 1440 ? `${Math.floor(ago/60)}h` : `${Math.floor(ago/1440)}d`;
                  return (
                    <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-3 text-white font-mono text-sm font-bold">{t.ticket_number}</td>
                      <td className="px-3 py-3">
                        <span className={`text-[10px] px-2 py-1 rounded font-medium ${s.color}/20 ${s.text}`}>{s.label}</span>
                      </td>
                      <td className="px-3 py-3 text-zinc-300 text-sm">{t.user_company}</td>
                      <td className="px-3 py-3 text-white text-sm">{t.vehicle_brand} {t.vehicle_model}</td>
                      <td className="px-3 py-3 text-zinc-400 text-sm font-mono">{t.license_plate}</td>
                      <td className="px-3 py-3 text-zinc-300 text-xs">{t.service_name}</td>
                      <td className="px-3 py-3 text-zinc-500 text-xs font-mono">{t.ecu_type}</td>
                      <td className="px-3 py-3">
                        <span className={`text-xs font-mono ${ago > 120 ? 'text-red-400' : ago > 60 ? 'text-yellow-400' : 'text-green-400'}`}>
                          {timeStr}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitorPage;
